import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Messages } from 'primereact/messages';
import { ProgressSpinner } from '../../../components/PrimereactComponents';
import { getApiClient } from '../../../api';
import type { BodyTranslationsResponse, BodyTranslationsEditRequest } from '../../../api'
import { getErrorMessage } from '../../../utils/errors';
import { showErrorMessage, showSuccessMessage } from '../../utils/messages';
import { LanguageEntityTranslationsForm } from './components/TranslationEntityForm';

export default function TranslationBodiesEdit(): React.ReactElement | null {
    const { languageCode } = useParams<{ languageCode: string }>();
    const msgs = useRef<Messages>(null!);

    const [translations, setTranslations] = useState<BodyTranslationsResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [processing, setProcessing] = useState<boolean>(false);

    useEffect(() => {
        if (!languageCode) {
            setLoading(false);
            return;
        }

        const fetchTranslations = async () => {
            try {
                const data = await getApiClient().getAdminBodyTranslationsByLanguageCode(languageCode);
                setTranslations(data);
            } catch (error) {
                showErrorMessage(msgs, getErrorMessage(error) ?? 'Nepodařilo se načíst překlady orgánů');
            } finally {
                setLoading(false);
            }
        };

        fetchTranslations();
    }, [languageCode]);

    const onSubmit = async (data: BodyTranslationsEditRequest): Promise<void> => {
        if (!languageCode) return;

        setProcessing(true);
        try {
            await getApiClient().putAdminBodyTranslationsByLanguageCode(languageCode, data);
            showSuccessMessage(msgs, 'Překlady orgánů byly úspěšně uloženy');
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (error) {
            showErrorMessage(msgs, getErrorMessage(error) ?? 'Nepodařilo se uložit překlady orgánů', { replace: true });
        } finally {
            setProcessing(false);
        }
    };

    if (loading) return <ProgressSpinner />;

    return (
        <>
            <Messages ref={msgs} className='page__messages' />

            <LanguageEntityTranslationsForm
                translations={translations}
                itemsFieldName="bodies"
                identityColumns={[
                    { field: 'organ_id', header: 'ID orgánu' },
                    { field: 'municipality_text_id', header: 'Municipality' },
                    { field: 'body_type_code', header: 'Typ orgánu' }
                ]}
                translationColumns={[
                    { field: 'nazev', header: 'Název', required: true, inputType: 'textarea', rows: 2 },
                    { field: 'nazev_genitiv', header: 'Název (2. pád, volitelné)', required: false, inputType: 'textarea', rows: 2 },
                    { field: 'nazev_aplikace', header: 'Název aplikace', required: false, inputType: 'textarea', rows: 3 },
                    { field: 'popis_aplikace', header: 'Popis aplikace', required: false, inputType: 'textarea', rows: 3 }
                ]}
                processing={processing}
                onSubmit={onSubmit}
                buildPayload={(formData) => ({
                    bodies: formData.bodies.map((item) => ({
                        organ_id: item.organ_id,
                        nazev: item.nazev ?? '',
                        nazev_genitiv: item.nazev_genitiv ?? null,
                        nazev_aplikace: item.nazev_aplikace,
                        popis_aplikace: item.popis_aplikace
                    }))
                })}
            />
        </>
    );
}
